import { createSlice, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import _get from 'lodash/get';

interface ModalState {
  isSomeModalOpen: boolean;
  modals: {
    [modalName: string]: {
      show: boolean;
      payload?: any;
    };
  }
}

// Initial state
const initialState: ModalState = {
  isSomeModalOpen: false,
  modals: {},
};

// Slice
export const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    toggleModal: {
      reducer: (state, action: PayloadAction<{ name: string; show: boolean; payload?: any }>) => {
        const { name, show, payload } = action.payload;
        state.modals[name] = {
          show,
          payload: show ? payload : undefined,
        };
        state.isSomeModalOpen = Object.keys(state.modals).some(
          (modalName) => state.modals[modalName].show,
        );
      },
      prepare: (name: string, show: boolean, payload?: any) => ({
        payload: { name, show, payload },
      }),
    },
    openModal: {
      reducer: (state, action: PayloadAction<{ name: string; payload?: any }>) => {
        const { name, payload } = action.payload;
        state.modals[name] = {
          show: true,
          payload,
        };
        state.isSomeModalOpen = true;
      },
      prepare: (name: string, payload?: any) => ({
        payload: { name, payload },
      }),
    },
    closeModal: {
      reducer: (state, action: PayloadAction<{ name: string }>) => {
        const { name } = action.payload;
        state.modals[name] = {
          show: false,
          payload: undefined,
        };
        state.isSomeModalOpen = Object.keys(state.modals).some((modalName) => state.modals[modalName].show);
      },
      prepare: (name: string) => ({
        payload: { name },
      }),
    },
  },
});

// Export actions
export const {
  toggleModal,
  openModal,
  closeModal,
} = modalSlice.actions;

// Selectors
export const getModal = (state: any) => _get(state, 'modal', {});

export const getModalByName = (modalName: string) =>
  createSelector(
    (state: any) => state.modal.modals,
    (modals) => modals[modalName] || { show: false, payload: null },
  );

export const getIsSomeModalOpen = (state: any) => state.modal.isSomeModalOpen;

// Export reducer
export default modalSlice.reducer;
